import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../components/profile-components/Navbar";
import Topbar from "../../components/profile-components/Topbar";
import "./Workflows.css";
import "../SalesPages.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://agent-book-crm-backend.onrender.com";

function formatLabel(value = "") {
  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function summarizeTrigger(trigger) {
  if (!trigger) return "—";

  const type = trigger.type || "";
  const config = trigger.config || {};

  if (type === "recordType") {
    return Array.isArray(config.values) && config.values.length
      ? `Record Type: ${config.values.join(", ")}`
      : "Record Type";
  }

  if (type === "onDate") {
    const days = config.days ? `${config.days} day${Number(config.days) === 1 ? "" : "s"}` : "0 days";
    const direction = config.direction || "after";
    const dateType = config.dateType ? formatLabel(config.dateType) : "Selected Date";
    return `On Date: ${days} ${direction} ${dateType}`;
  }

  if (config.value) {
    return `${formatLabel(type)}: ${config.value}`;
  }

  if (Array.isArray(config.values) && config.values.length) {
    return `${formatLabel(type)}: ${config.values.join(", ")}`;
  }

  return formatLabel(type);
}

function summarizeAction(action, index) {
  if (!action) return "—";

  const stepLabel = `Step ${index + 1}: ${formatLabel(action.action_type)}`;

  if (action.action_type === "send_email") {
    return `${stepLabel}${action.send_to ? ` → ${formatLabel(action.send_to)}` : ""}`;
  }

  if (action.action_type === "send_sms") {
    return stepLabel;
  }

  if (action.action_type === "create_task") {
    return action.task_title ? `${stepLabel} — ${action.task_title}` : stepLabel;
  }

  if (action.action_type === "add_tag" || action.action_type === "remove_tag") {
    return action.tag ? `${stepLabel} — ${action.tag}` : stepLabel;
  }

  if (action.action_type === "update_status") {
    return action.status ? `${stepLabel} — ${action.status}` : stepLabel;
  }

  return stepLabel;
}

export default function WorkflowsPage() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyWorkflowId, setBusyWorkflowId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchWorkflows();
  }, []);

  async function fetchWorkflows() {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE_URL}/api/automations/workflows`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Failed to load workflows");

      const data = await res.json();
      setWorkflows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("❌ Failed to load workflows:", err);
      setError("Unable to load workflows");
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(id) {
    navigate(`/automations/workflows/${id}`);
  }

  async function handleDelete(id) {
    if (!window.confirm("Are you sure you want to delete this workflow?")) {
      return;
    }

    try {
      setBusyWorkflowId(id);

      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE_URL}/api/automations/workflows/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Failed to delete workflow");

      await fetchWorkflows();
    } catch (err) {
      console.error("❌ Delete failed:", err);
      alert("Failed to delete workflow.");
    } finally {
      setBusyWorkflowId(null);
    }
  }

  async function handleCopy(workflow) {
    try {
      setBusyWorkflowId(workflow.id);

      const token = localStorage.getItem("token");

      const payload = {
        name: `${workflow.name} (Copy)`,
        triggers: workflow.triggers || [],
        actions: workflow.actions || [],
        is_active: 0,
      };

      const res = await fetch(`${API_BASE_URL}/api/automations/workflows`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to copy workflow");

      await fetchWorkflows();
    } catch (err) {
      console.error("❌ Copy failed:", err);
      alert("Failed to copy workflow.");
    } finally {
      setBusyWorkflowId(null);
    }
  }

  async function handleToggleActive(workflow) {
    try {
      setBusyWorkflowId(workflow.id);

      const token = localStorage.getItem("token");
      const updatedIsActive = workflow.is_active === 1 ? 0 : 1;

      const payload = {
        name: workflow.name,
        triggers: workflow.triggers || [],
        actions: workflow.actions || [],
        is_active: updatedIsActive,
      };

      const res = await fetch(
        `${API_BASE_URL}/api/automations/workflows/${workflow.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error("Failed to update workflow");

      setWorkflows((prev) =>
        prev.map((w) =>
          w.id === workflow.id ? { ...w, is_active: updatedIsActive } : w
        )
      );
    } catch (err) {
      console.error("❌ Toggle failed:", err);
      alert("Failed to update workflow status.");
    } finally {
      setBusyWorkflowId(null);
    }
  }

  const activeCount = useMemo(
    () => workflows.filter((workflow) => workflow.is_active === 1).length,
    [workflows]
  );

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">Workflows</h1>
            <p className="sales-subtitle">
              Manage workflow automations, review triggers and actions, and control which workflows are active.
            </p>

            <div className="sales-hero-meta">
              <span className="sales-chip sales-chip-neutral">
                {workflows.length} Workflow{workflows.length === 1 ? "" : "s"}
              </span>
              <span className="sales-chip sales-chip-outline">
                {activeCount} Active
              </span>
            </div>
          </div>

          <div className="sales-header-actions">
            <Link to="/automations/workflows/new" className="sales-primary-btn">
              + Create Workflow
            </Link>
          </div>
        </div>

        {loading && (
          <div className="sales-table-card">
            <div className="sales-table-wrap">
              <div className="workflows-empty-state">Loading workflows...</div>
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="sales-table-card">
            <div className="sales-table-wrap">
              <div className="workflows-empty-state workflows-error-state">
                {error}
              </div>
            </div>
          </div>
        )}

        {!loading && !error && !workflows.length && (
          <div className="sales-table-card">
            <div className="sales-table-wrap">
              <div className="workflows-empty-state">
                No workflows created yet.
              </div>
            </div>
          </div>
        )}

        {!loading && !error && !!workflows.length && (
          <div className="sales-table-card">
            <div className="sales-table-header">
              <div>
                <h2>Workflow Library</h2>
                <p>Review workflow setup, trigger logic, and active status.</p>
              </div>
            </div>

            <div className="sales-table-wrap">
              <table className="sales-table workflows-table-modern">
                <thead>
                  <tr>
                    <th>Workflow</th>
                    <th>Triggers</th>
                    <th>Actions</th>
                    <th>Active</th>
                    <th>Options</th>
                  </tr>
                </thead>

                <tbody>
                  {workflows.map((workflow) => {
                    const triggerCount = Array.isArray(workflow.triggers)
                      ? workflow.triggers.length
                      : 0;

                    const actionCount = Array.isArray(workflow.actions)
                      ? workflow.actions.length
                      : 0;

                    const visibleTriggers = (workflow.triggers || []).slice(0, 2);
                    const visibleActions = (workflow.actions || []).slice(0, 2);

                    return (
                      <tr key={workflow.id}>
                        <td className="workflow-name-cell">
                          <div className="workflow-name-block">
                            <button
                              type="button"
                              className="workflow-name-link"
                              onClick={() => handleEdit(workflow.id)}
                            >
                              {workflow.name}
                            </button>

                            <div className="workflow-meta-subtext">
                              {triggerCount} trigger{triggerCount === 1 ? "" : "s"} • {actionCount} action{actionCount === 1 ? "" : "s"}
                            </div>
                          </div>
                        </td>

                        <td>
                          {triggerCount ? (
                            <div className="workflow-text-list">
                              {visibleTriggers.map((trigger, index) => (
                                <div
                                  key={`${workflow.id}-trigger-${index}`}
                                  className="workflow-text-item"
                                >
                                  {summarizeTrigger(trigger)}
                                </div>
                              ))}

                              {triggerCount > 2 && (
                                <div className="workflow-text-more">
                                  +{triggerCount - 2} more
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="workflow-text-empty">No triggers</div>
                          )}
                        </td>

                        <td>
                          {actionCount ? (
                            <div className="workflow-text-list">
                              {visibleActions.map((action, index) => (
                                <div
                                  key={`${workflow.id}-action-${index}`}
                                  className="workflow-text-item"
                                >
                                  {summarizeAction(action, index)}
                                </div>
                              ))}

                              {actionCount > 2 && (
                                <div className="workflow-text-more">
                                  +{actionCount - 2} more
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="workflow-text-empty">No actions</div>
                          )}
                        </td>

                        <td className="workflow-active-cell">
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={workflow.is_active === 1}
                              onChange={() => handleToggleActive(workflow)}
                              disabled={busyWorkflowId === workflow.id}
                            />
                            <span className="slider round"></span>
                          </label>
                        </td>

                        <td>
                          <div className="workflow-options-menu">
                            <button
                              type="button"
                              className="sales-secondary-btn workflow-table-btn"
                              onClick={() => handleEdit(workflow.id)}
                              disabled={busyWorkflowId === workflow.id}
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              className="sales-secondary-btn workflow-table-btn"
                              onClick={() => handleCopy(workflow)}
                              disabled={busyWorkflowId === workflow.id}
                            >
                              Copy
                            </button>

                            <button
                              type="button"
                              className="sales-danger-btn workflow-table-btn"
                              onClick={() => handleDelete(workflow.id)}
                              disabled={busyWorkflowId === workflow.id}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}